import { useCallback, useEffect, useMemo } from "react"
import PropTypes from "prop-types"
import { map } from "lodash"
import { useDispatch, useSelector } from "react-redux"
import { useQueryClient } from "@tanstack/react-query"
import { Creators } from "model/apiWrapper"
import { useAccountAlias } from "resources/account"
import { combineTanStackStatuses } from "helpers/data-statuses"
import useCombinedQueries from "hooks/combine-queries"
import { getIsIntradayAnalyticsEnabled } from "selectors/features"
import {
  getInstrumentSuffixForIntradayContributionPrefix,
  mergeContributorTrees,
} from "./contributors-data-integrator.utils"
import { useIntradayContributorsEnhancement } from "./hooks/use-intraday-contributors-enhancement"

const EMPTY_ARRAY = []

export {
  getNormalPerformanceAutoRefreshWarningState,
  shouldAutoInvalidateIntradayContributionGathererOnPricingUpdate,
  shouldSubscribeToIntradayPriceTicks,
  mergeContributorTrees,
} from "./contributors-data-integrator.utils"

export default function ContributorsDataIntegrator(props) {
  const {
    gatherers,
    metrics,
    Renderer,
    onModelProviderIds,
    rendererProps = {},
    instrumentsWithMetadata,
  } = props
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const account = useAccountAlias()
  const isIntradayAnalyticsEnabled = useSelector(getIsIntradayAnalyticsEnabled)
  const nonIntradayGatherers = useMemo(() => (gatherers || []).filter((gatherer) => gatherer.type !== "intraday"), [gatherers])
  const intradayGatherers = useMemo(() => (gatherers || []).filter((gatherer) => gatherer.type === "intraday"), [gatherers])
  const intradayContributionQueryIndexes = useMemo(() => {
    return nonIntradayGatherers.reduce((indexes, gatherer, index) => {
      if (getInstrumentSuffixForIntradayContributionPrefix(gatherer.prefix) != null) {
        indexes.push(index)
      }
      return indexes
    }, [])
  }, [nonIntradayGatherers])

  const combine = useCallback((results) => {
    const status = combineTanStackStatuses(results)
    const dataResults = map(results, (result) => result?.data).filter(Boolean)
    const data = dataResults.length > 0 ? mergeContributorTrees(dataResults) : null
    let intradayContributionPerformanceLastUpdatedAt = null
    let isFetchingIntradayContributionQueries = false
    let isAnyQueryPending = false

    for (let queryIndex = 0; queryIndex < results.length; queryIndex++) {
      const queryResult = results[queryIndex]
      if (!queryResult) {
        continue
      }

      if (queryResult.status === "pending") {
        isAnyQueryPending = true
      }

      const isContributionQuery = intradayContributionQueryIndexes.includes(queryIndex)
      if (isContributionQuery) {
        if (queryResult.isFetching && queryResult.dataUpdatedAt > 0) {
          isFetchingIntradayContributionQueries = true
        }
        if (queryResult.dataUpdatedAt != null) {
          intradayContributionPerformanceLastUpdatedAt = Math.max(
            intradayContributionPerformanceLastUpdatedAt ?? 0,
            queryResult.dataUpdatedAt,
          )
        }
      }
    }

    return {
      status,
      data,
      intradayContributionPerformanceLastUpdatedAt,
      isFetchingIntradayContributionQueries,
      isAnyQueryPending,
    }
  }, [intradayContributionQueryIndexes])

  const queries = map(nonIntradayGatherers, ({ key, query, variables, select }) => ({
    queryKey: [key],
    queryFn: () => new Promise((resolve, reject) => {
      dispatch(Creators.graphql(account, query, variables, resolve, reject))
    }),
    select,
  }))

  const {
    status,
    data,
    intradayContributionPerformanceLastUpdatedAt,
    isFetchingIntradayContributionQueries,
    isAnyQueryPending,
  } = useCombinedQueries(queries, combine)

  const {
    data: enhancedData,
    status: enhancedStatus,
    metrics: enhancedMetrics,
    modelProviderIds,
    isIntradayContributionRefreshing,
  } = useIntradayContributorsEnhancement({
    nonIntradayGatherers,
    intradayGatherers,
    metrics,
    data,
    status,
    intradayContributionPerformanceLastUpdatedAt,
    isFetchingIntradayContributionQueries,
    isAnyQueryPending,
    isIntradayAnalyticsEnabled,
    instrumentsWithMetadata,
    queryClient,
  })

  useEffect(() => {
    if (typeof onModelProviderIds !== "function") {
      return
    }
    onModelProviderIds(modelProviderIds || EMPTY_ARRAY)
  }, [modelProviderIds, onModelProviderIds])

  return (
    <Renderer
      status={enhancedStatus}
      data={enhancedData}
      metrics={enhancedMetrics}
      isIntradayContributionRefreshing={isIntradayContributionRefreshing}
      {...rendererProps}
    />
  )
}

const GRAPHQL_GATHERER_SHAPE = PropTypes.shape({
  key: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])).isRequired,
  query: PropTypes.string.isRequired,
  variables: PropTypes.object.isRequired,
  select: PropTypes.func.isRequired,
})

const INTRADAY_GATHERER_SHAPE = PropTypes.shape({
  type: PropTypes.oneOf(["intraday"]).isRequired,
  key: PropTypes.arrayOf(PropTypes.string).isRequired,
  modelId: PropTypes.string.isRequired,
  prefix: PropTypes.string.isRequired,
  streamType: PropTypes.oneOf(["prices", "standalone-performance"]).isRequired,
  period: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
})

ContributorsDataIntegrator.propTypes = {
  gatherers: PropTypes.arrayOf(PropTypes.oneOfType([GRAPHQL_GATHERER_SHAPE, INTRADAY_GATHERER_SHAPE])).isRequired,
  metrics: PropTypes.array.isRequired,
  Renderer: PropTypes.func.isRequired,
  rendererProps: PropTypes.object,
  onModelProviderIds: PropTypes.func,
  instrumentsWithMetadata: PropTypes.object,
}
